import { ThemeColors } from '@/types/theme';
import { 
  getContrastingTextColor, 
  generateHoverColor, 
  getColorContrast 
} from './themeUtils';

export interface SimpleThemeColors {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  foreground: string;
  destructive: string;
}

/**
 * Generate a complete theme from simple base colors
 * @param simpleColors - Basic color palette
 * @returns Complete theme colors with all properties
 */
export const generateThemeFromSimple = (simpleColors: SimpleThemeColors): ThemeColors => {
  const {
    primary,
    secondary,
    accent,
    background,
    foreground,
    destructive
  } = simpleColors;

  // Generate hover colors (darker versions)
  const primaryHover = generateHoverColor(primary, 0.15);
  const secondaryHover = generateHoverColor(secondary, 0.15);
  const accentHover = generateHoverColor(accent, 0.15);

  // Generate light variants
  const primaryLight = generateLightVariant(primary, 0.9);
  const accentLight = generateLightVariant(accent, 0.9);

  // Generate contrasting text colors
  const primaryForeground = getContrastingTextColor(primary);
  const secondaryForeground = getContrastingTextColor(secondary);
  const accentForeground = getContrastingTextColor(accent);
  const destructiveForeground = getContrastingTextColor(destructive);

  // Generate muted colors based on background
  const muted = generateMutedColor(background);
  const mutedForeground = getContrastingTextColor(muted);

  // Generate border color based on background
  const border = generateBorderColor(background);

  // Generate input color (usually same as background or slightly different)
  const input = generateInputColor(background);

  // Generate ring color (usually primary)
  const ring = primary;

  // Generate card colors
  const card = generateCardColor(background);
  const cardForeground = getContrastingTextColor(card);

  // Generate popover colors
  const popover = generatePopoverColor(background);
  const popoverForeground = getContrastingTextColor(popover);

  // Generate sidebar colors (adaptive based on theme)
  const sidebarColor = generateSidebarColor(primary, background);
  const sidebarTextColor = getContrastingTextColor(sidebarColor);
  const sidebarHoverBgColor = generateHoverColor(sidebarColor, 0.15);
  const sidebarHoverTextColor = getContrastingTextColor(sidebarHoverBgColor);

  return {
    // Primary colors
    primary,
    primaryHover,
    primaryLight,
    primaryForeground,
    
    // Secondary colors
    secondary,
    secondaryHover,
    secondaryForeground,
    
    // Accent colors
    accent,
    accentHover,
    accentForeground,
    
    // Base colors
    background,
    foreground,
    
    // Muted colors
    muted,
    mutedForeground,
    
    // UI colors
    border,
    input,
    ring,
    
    // Destructive colors
    destructive,
    destructiveForeground,
    
    // Card colors
    card,
    cardForeground,
    
    // Popover colors
    popover,
    popoverForeground,
    
    // Sidebar colors
    sidebarColor,
    sidebarTextColor,
    sidebarHoverBgColor,
    sidebarHoverTextColor,
  };
};

/**
 * Generate a light variant of a color
 * @param color - Base color in hex format
 * @param lightness - Lightness factor (0-1)
 * @returns Light variant of the color
 */
const generateLightVariant = (color: string, lightness: number = 0.9): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Lighten the color
  const newR = Math.min(255, Math.floor(r + (255 - r) * lightness));
  const newG = Math.min(255, Math.floor(g + (255 - g) * lightness));
  const newB = Math.min(255, Math.floor(b + (255 - b) * lightness));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Generate muted color based on background
 * @param background - Background color
 * @returns Muted color
 */
const generateMutedColor = (background: string): string => {
  const isLight = getColorContrast(background) === 'light';
  return isLight ? '#f5f5f5' : '#374151';
};

/**
 * Generate border color based on background
 * @param background - Background color
 * @returns Border color
 */
const generateBorderColor = (background: string): string => {
  const isLight = getColorContrast(background) === 'light';
  return isLight ? '#e5e7eb' : '#4b5563';
};

/**
 * Generate input color based on background
 * @param background - Background color
 * @returns Input color
 */
const generateInputColor = (background: string): string => {
  const isLight = getColorContrast(background) === 'light';
  return isLight ? '#ffffff' : '#1f2937';
};

/**
 * Generate card color based on background
 * @param background - Background color
 * @returns Card color
 */
const generateCardColor = (background: string): string => {
  const isLight = getColorContrast(background) === 'light';
  return isLight ? '#ffffff' : '#1f2937';
};

/**
 * Generate popover color based on background
 * @param background - Background color
 * @returns Popover color
 */
const generatePopoverColor = (background: string): string => {
  const isLight = getColorContrast(background) === 'light';
  return isLight ? '#ffffff' : '#1f2937';
};

/**
 * Generate sidebar color based on primary color and background
 * @param primary - Primary color of the theme
 * @param background - Background color
 * @returns Sidebar color
 */
const generateSidebarColor = (primary: string, background: string): string => {
  const isLight = getColorContrast(background) === 'light';
  
  if (isLight) {
    // For light backgrounds, create a dark sidebar that complements the primary color
    // Use a dark variant of the primary color or a neutral dark color
    const primaryIsDark = getColorContrast(primary) === 'dark';
    
    if (primaryIsDark) {
      // If primary is dark, use it as the sidebar color
      return primary;
    } else {
      // If primary is light, use a dark neutral color
      return '#1f2937'; // Dark gray that works well with most themes
    }
  } else {
    // For dark backgrounds, use a lighter sidebar
    const primaryIsLight = getColorContrast(primary) === 'light';
    
    if (primaryIsLight) {
      // If primary is light, use it as the sidebar color
      return primary;
    } else {
      // If primary is dark, use a light neutral color
      return '#f3f4f6'; // Light gray that works well with most themes
    }
  }
};

/**
 * Extract simple colors from a complete theme
 * @param themeColors - Complete theme colors
 * @returns Simple color palette
 */
export const extractSimpleColors = (themeColors: ThemeColors): SimpleThemeColors => {
  return {
    primary: themeColors.primary,
    secondary: themeColors.secondary,
    accent: themeColors.accent,
    background: themeColors.background,
    foreground: themeColors.foreground,
    destructive: themeColors.destructive,
  };
};

/**
 * Validate simple theme colors
 * @param simpleColors - Simple color palette
 * @returns Validation result
 */
export const validateSimpleColors = (simpleColors: SimpleThemeColors): {
  isValid: boolean;
  issues: string[];
} => {
  const issues: string[] = [];

  // Check if all colors are provided
  Object.entries(simpleColors).forEach(([key, value]) => {
    if (!value || value.trim() === '') {
      issues.push(`${key} color is required`);
    }
  });

  // Check for valid hex colors
  Object.entries(simpleColors).forEach(([key, value]) => {
    if (value && !/^#[0-9A-F]{6}$/i.test(value)) {
      issues.push(`${key} color must be a valid hex color (e.g., #ff0000)`);
    }
  });

  return {
    isValid: issues.length === 0,
    issues,
  };
};

/**
 * Generate theme preview with sample components
 * @param simpleColors - Simple color palette
 * @returns Preview data
 */
/**
 * Generate a theme with random variation for regeneration
 * @param simpleColors - Basic color palette
 * @returns Complete theme colors with variation
 */
export const generateThemeWithVariation = (simpleColors: SimpleThemeColors): ThemeColors => {
  // Create slightly modified base colors for regeneration
  const modifiedSimpleColors: SimpleThemeColors = {
    primary: adjustColorBrightness(simpleColors.primary, Math.random() * 0.2 - 0.1), // ±10%
    secondary: adjustColorBrightness(simpleColors.secondary, Math.random() * 0.2 - 0.1), // ±10%
    accent: adjustColorBrightness(simpleColors.accent, Math.random() * 0.2 - 0.1), // ±10%
    background: simpleColors.background, // Keep background the same
    foreground: simpleColors.foreground, // Keep foreground the same
    destructive: simpleColors.destructive, // Keep destructive the same
  };
  
  return generateThemeFromSimple(modifiedSimpleColors);
};

/**
 * Adjust color brightness by a percentage
 * @param color - Hex color string
 * @param adjustment - Adjustment factor (-1 to 1)
 * @returns Modified hex color
 */
const adjustColorBrightness = (color: string, adjustment: number): string => {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Apply adjustment
  const factor = 1 + adjustment;
  const newR = Math.max(0, Math.min(255, Math.round(r * factor)));
  const newG = Math.max(0, Math.min(255, Math.round(g * factor)));
  const newB = Math.max(0, Math.min(255, Math.round(b * factor)));
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

export const generateThemePreview = (simpleColors: SimpleThemeColors) => {
  const themeColors = generateThemeFromSimple(simpleColors);
  
  return {
    colors: themeColors,
    preview: {
      primary: {
        background: themeColors.primary,
        text: themeColors.primaryForeground,
        hover: themeColors.primaryHover,
      },
      secondary: {
        background: themeColors.secondary,
        text: themeColors.secondaryForeground,
        hover: themeColors.secondaryHover,
      },
      accent: {
        background: themeColors.accent,
        text: themeColors.accentForeground,
        hover: themeColors.accentHover,
      },
      destructive: {
        background: themeColors.destructive,
        text: themeColors.destructiveForeground,
      },
      card: {
        background: themeColors.card,
        text: themeColors.cardForeground,
        border: themeColors.border,
      },
      sidebar: {
        background: themeColors.sidebarColor,
        text: themeColors.sidebarTextColor,
        hover: themeColors.sidebarHoverBgColor,
        hoverText: themeColors.sidebarHoverTextColor,
      },
    },
  };
}; 